import { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Switch,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import {
    useLocalPushSchedules,
    useCreateLocalPushSchedule,
    useUpdateLocalPushSchedule,
    useDeleteLocalPushSchedule,
} from '../../hooks/useLocalPush';
import {
    LocalPushSchedule,
    CreateLocalPushScheduleRequest,
    UpdateLocalPushScheduleRequest,
} from '../../types/localPush';

interface ScheduleFormData {
    dayOffset: number;
    sendHour: number;
    sendMinute: number;
    title: string;
    body: string;
    translations: { languageCode: string; title: string; body: string }[];
    enabled: boolean;
    sortOrder: number;
}

const defaultFormData: ScheduleFormData = {
    dayOffset: 0,
    sendHour: 10,
    sendMinute: 0,
    title: '',
    body: '',
    translations: [],
    enabled: true,
    sortOrder: 0,
};

export default function LocalPushScheduleList() {
    const { data: schedules, isLoading } = useLocalPushSchedules();
    const createSchedule = useCreateLocalPushSchedule();
    const updateSchedule = useUpdateLocalPushSchedule();
    const deleteSchedule = useDeleteLocalPushSchedule();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState<ScheduleFormData>(defaultFormData);

    const handleOpen = (schedule?: LocalPushSchedule) => {
        if (schedule) {
            setEditingId(schedule.id);
            setFormData({
                dayOffset: schedule.dayOffset,
                sendHour: schedule.sendHour,
                sendMinute: schedule.sendMinute,
                title: schedule.title,
                body: schedule.body,
                translations: schedule.translations ?? [],
                enabled: schedule.enabled,
                sortOrder: schedule.sortOrder,
            });
        } else {
            setEditingId(null);
            setFormData(defaultFormData);
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditingId(null);
    };

    const handleSave = async () => {
        if (editingId) {
            await updateSchedule.mutateAsync({ id: editingId, data: formData as UpdateLocalPushScheduleRequest });
        } else {
            await createSchedule.mutateAsync(formData as CreateLocalPushScheduleRequest);
        }
        handleClose();
    };

    const handleDelete = (id: number) => {
        if (confirm('이 스케줄을 삭제하시겠습니까?')) {
            deleteSchedule.mutate(id);
        }
    };

    const handleToggleEnabled = (schedule: LocalPushSchedule) => {
        updateSchedule.mutate({
            id: schedule.id,
            data: {
                dayOffset: schedule.dayOffset,
                sendHour: schedule.sendHour,
                sendMinute: schedule.sendMinute,
                title: schedule.title,
                body: schedule.body,
                translations: schedule.translations ?? [],
                enabled: !schedule.enabled,
                sortOrder: schedule.sortOrder,
            },
        });
    };

    const isSaving = createSchedule.isPending || updateSchedule.isPending;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">로컬 푸시 스케줄</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                    스케줄 추가
                </Button>
            </Box>

            <Paper>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Day</TableCell>
                                    <TableCell>발송 시각</TableCell>
                                    <TableCell>제목 (KO)</TableCell>
                                    <TableCell>본문 (KO)</TableCell>
                                    <TableCell align="center">활성</TableCell>
                                    <TableCell>정렬</TableCell>
                                    <TableCell align="center">관리</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(schedules ?? []).map((schedule) => (
                                    <TableRow key={schedule.id}>
                                        <TableCell>D+{schedule.dayOffset}</TableCell>
                                        <TableCell>
                                            {String(schedule.sendHour).padStart(2, '0')}:
                                            {String(schedule.sendMinute).padStart(2, '0')}
                                        </TableCell>
                                        <TableCell>
                                            {schedule.title}
                                        </TableCell>
                                        <TableCell>
                                            {schedule.body}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                checked={schedule.enabled}
                                                onChange={() => handleToggleEnabled(schedule)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{schedule.sortOrder}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => handleOpen(schedule)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(schedule.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!schedules || schedules.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="text.secondary">
                                                등록된 스케줄이 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? '스케줄 수정' : '스케줄 추가'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box display="flex" gap={2}>
                            <TextField
                                label="Day Offset"
                                type="number"
                                value={formData.dayOffset}
                                onChange={(e) => setFormData({ ...formData, dayOffset: parseInt(e.target.value) || 0 })}
                                slotProps={{ htmlInput: { min: 0, max: 30 } }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="시"
                                type="number"
                                value={formData.sendHour}
                                onChange={(e) => setFormData({ ...formData, sendHour: parseInt(e.target.value) || 0 })}
                                slotProps={{ htmlInput: { min: 0, max: 23 } }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label="분"
                                type="number"
                                value={formData.sendMinute}
                                onChange={(e) => setFormData({ ...formData, sendMinute: parseInt(e.target.value) || 0 })}
                                slotProps={{ htmlInput: { min: 0, max: 59 } }}
                                sx={{ flex: 1 }}
                            />
                        </Box>

                        <TextField
                            label="정렬 순서"
                            type="number"
                            value={formData.sortOrder}
                            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                            size="small"
                        />

                        <TextField
                            label="제목 (한글)"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            fullWidth
                            placeholder="오늘의 학습을 시작해보세요!"
                        />
                        <TextField
                            label="본문 (한글)"
                            value={formData.body}
                            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                            fullWidth
                            placeholder="매일 조금씩 학습하면 실력이 쑥쑥 올라요"
                        />

                        {editingId && (
                            <Box sx={{ mt: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">추가 언어 번역 (선택)</Typography>
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            setFormData({
                                                ...formData,
                                                translations: [
                                                    ...formData.translations,
                                                    { languageCode: '', title: '', body: '' },
                                                ],
                                            })
                                        }
                                    >
                                        번역 추가
                                    </Button>
                                </Box>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    {formData.translations.map((translation, index) => (
                                        <Paper key={`translation-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                                            <Box display="flex" gap={1} mb={1}>
                                                <TextField
                                                    label="언어 코드"
                                                    size="small"
                                                    value={translation.languageCode}
                                                    onChange={(e) => {
                                                        const next = [...formData.translations];
                                                        next[index] = { ...next[index], languageCode: e.target.value };
                                                        setFormData({ ...formData, translations: next });
                                                    }}
                                                    placeholder="en"
                                                    sx={{ width: 120 }}
                                                />
                                                <TextField
                                                    label="제목"
                                                    size="small"
                                                    value={translation.title}
                                                    onChange={(e) => {
                                                        const next = [...formData.translations];
                                                        next[index] = { ...next[index], title: e.target.value };
                                                        setFormData({ ...formData, translations: next });
                                                    }}
                                                    fullWidth
                                                />
                                            </Box>
                                            <Box display="flex" gap={1}>
                                                <TextField
                                                    label="본문"
                                                    size="small"
                                                    value={translation.body}
                                                    onChange={(e) => {
                                                        const next = [...formData.translations];
                                                        next[index] = { ...next[index], body: e.target.value };
                                                        setFormData({ ...formData, translations: next });
                                                    }}
                                                    fullWidth
                                                />
                                                <Button
                                                    color="error"
                                                    onClick={() => {
                                                        const next = formData.translations.filter((_, i) => i !== index);
                                                        setFormData({ ...formData, translations: next });
                                                    }}
                                                >
                                                    삭제
                                                </Button>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>취소</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={isSaving || !formData.title.trim() || !formData.body.trim()}
                    >
                        {isSaving ? '저장 중...' : '저장'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
